import { useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';

export interface TocHeading {
  level: number;
  text: string;
  id: string;
}

interface ArticleContentProps {
  content: Record<string, unknown>;
  onTocReady?: (headings: TocHeading[]) => void;
}

// Helper to extract content in a format TipTap can use
function getEditorContent(body: Record<string, unknown>): string | Record<string, unknown> {
  // If body has an 'html' property, return the HTML string
  if (typeof body?.html === 'string') {
    return body.html;
  }
  // Otherwise, assume it's TipTap JSON format
  return body;
}

export function ArticleContent({ content, onTocReady }: ArticleContentProps) {
  const editorContent = useMemo(() => getEditorContent(content), [content]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4],
        },
      }),
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'rounded-lg max-w-full h-auto',
          loading: 'lazy',
        },
      }),
    ],
    content: editorContent,
    editable: false,
    editorProps: {
      attributes: {
        class: 'prose prose-slate dark:prose-invert max-w-none focus:outline-none',
      },
    },
  });

  useEffect(() => {
    if (editor && content) {
      const newContent = getEditorContent(content);
      editor.commands.setContent(newContent);
    }
  }, [editor, content]);

  // Extract headings and notify parent
  const tableOfContents = useMemo(() => {
    if (!editor) return [];
    const headings: TocHeading[] = [];
    editor.state.doc.descendants((node) => {
      if (node.type.name === 'heading') {
        const text = node.textContent;
        const id = text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        headings.push({
          level: node.attrs.level,
          text,
          id,
        });
      }
    });
    return headings;
  }, [editor?.state.doc]);

  useEffect(() => {
    if (onTocReady) {
      onTocReady(tableOfContents);
    }
  }, [tableOfContents, onTocReady]);

  return <EditorContent editor={editor} />;
}
